import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, Edit2, X, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { groupByMonth } from '@/utils/groupByMonth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RMReceipt() {
  const [receipts, setReceipts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [collapsedMonths, setCollapsedMonths] = useState({});
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canModify = (entry) => user.role === 'admin' || entry.created_by === user.username;
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    steel_grade: '',
    section: '',
    qty_weight: '',
    invoice: '',
    mill_tc: ''
  });

  const fetchReceipts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/transactions/rm-receipt`, {
        
      });
      setReceipts(response.data);
    } catch (err) { console.error(err);
      toast.error('Failed to load RM receipts');
    }
  }, []);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      steel_grade: '',
      section: '',
      qty_weight: '',
      invoice: '',
      mill_tc: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (receipt) => {
    setFormData({
      date: new Date(receipt.date).toISOString().split('T')[0],
      steel_grade: receipt.steel_grade,
      section: receipt.section,
      qty_weight: receipt.qty_weight.toString(),
      invoice: receipt.invoice,
      mill_tc: receipt.mill_tc
    });
    setEditingId(receipt.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const data = {
        ...formData,
        date: new Date(formData.date).toISOString(),
        qty_weight: parseFloat(formData.qty_weight),
        created_by: user.username
      };

      if (editingId) {
        await axios.put(`${API}/transactions/rm-receipt/${editingId}`, data, {
          
        });
        toast.success('RM Receipt updated successfully');
      } else {
        await axios.post(`${API}/transactions/rm-receipt`, data, {
          
        });
        toast.success('RM Receipt created successfully');
      }
      resetForm();
      fetchReceipts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save receipt');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this receipt?')) return;
    try {
      await axios.delete(`${API}/transactions/rm-receipt/${id}`, {
        
      });
      toast.success('Receipt deleted successfully');
      fetchReceipts();
    } catch (error) {
      toast.error('Failed to delete receipt');
    }
  };

  const toggleMonth = (key) => setCollapsedMonths(prev => ({...prev, [key]: !prev[key]}));
  const monthGroups = groupByMonth(receipts);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="rm-receipt-title">RM RECEIPT</h1>
          <p className="text-sm text-gray-700 mt-2">Record raw material receipts</p>
        </div>
        <Button
          data-testid="toggle-rm-form-button"
          onClick={() => { if (showForm) resetForm(); else setShowForm(true); }}
          className="bg-accent text-accent-foreground hover:bg-accent-hover rounded-sm font-medium"
        >
          {showForm ? <><X className="w-4 h-4 mr-2" />CANCEL</> : <><Plus className="w-4 h-4 mr-2" />NEW RECEIPT</>}
        </Button>
      </div>

      {showForm && (
        <div className="bg-white border border-border shadow-sm p-6">
          <h2 className="text-2xl font-bold uppercase mb-6" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
            {editingId ? 'EDIT RM RECEIPT' : 'NEW RM RECEIPT'}
          </h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Date</Label>
              <Input
                data-testid="rm-date-input"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                required
                className="mt-1.5 rounded-none"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Steel Grade</Label>
              <Input
                data-testid="rm-steel-grade-input"
                value={formData.steel_grade}
                onChange={(e) => setFormData({...formData, steel_grade: e.target.value})}
                required
                className="mt-1.5 rounded-none"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Section</Label>
              <Input
                data-testid="rm-section-input"
                value={formData.section}
                onChange={(e) => setFormData({...formData, section: e.target.value})}
                required
                className="mt-1.5 rounded-none"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Qty (Weight)</Label>
              <Input
                data-testid="rm-qty-input"
                type="number"
                step="0.01"
                value={formData.qty_weight}
                onChange={(e) => setFormData({...formData, qty_weight: e.target.value})}
                required
                className="mt-1.5 rounded-none"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Invoice No</Label>
              <Input
                data-testid="rm-invoice-input"
                value={formData.invoice}
                onChange={(e) => setFormData({...formData, invoice: e.target.value})}
                required
                className="mt-1.5 rounded-none"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Mill TC</Label>
              <Input
                data-testid="rm-mill-tc-input"
                value={formData.mill_tc}
                onChange={(e) => setFormData({...formData, mill_tc: e.target.value})}
                required
                className="mt-1.5 rounded-none"
              />
            </div>
            <div className="col-span-3 flex justify-end pt-4">
              <Button
                data-testid="submit-rm-receipt-button"
                type="submit"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
              >
                {editingId ? 'UPDATE RECEIPT' : 'CREATE RECEIPT'}
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white border border-border shadow-sm">
        <div className="border-b border-border p-4 bg-gray-50/50">
          <h2 className="text-xl font-semibold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>RECENT RECEIPTS</h2>
        </div>
        {receipts.length === 0 ? (
          <div className="p-12 text-center text-gray-700">No receipts found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full caption-bottom text-sm">
              <thead>
                <tr className="border-b">
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Date</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Steel Grade</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Section</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Qty Weight</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Invoice</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Mill TC</th>
                  <th className="h-10 px-4 text-center align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {monthGroups.map(group => (
                  <React.Fragment key={`group-${group.key}`}>
                    <tr className="bg-gray-100 cursor-pointer" onClick={() => toggleMonth(group.key)}>
                      <td colSpan={7} className="p-3">
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-text-muted">
                          {collapsedMonths[group.key] ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          {group.label} ({group.entries.length} entries)
                        </div>
                      </td>
                    </tr>
                    {!collapsedMonths[group.key] && group.entries.map((receipt, idx) => (
                      <tr key={receipt.id || idx} className="border-b transition-colors hover:bg-muted/50" data-testid={`rm-receipt-row-${idx}`}>
                        <td className="p-4 align-middle font-mono text-xs">{new Date(receipt.date).toLocaleDateString()}</td>
                    <td className="p-4 align-middle">{receipt.steel_grade}</td>
                    <td className="p-4 align-middle">{receipt.section}</td>
                    <td className="p-4 align-middle text-right font-mono text-xs">{receipt.qty_weight.toFixed(2)}</td>
                    <td className="p-4 align-middle font-mono text-xs">{receipt.invoice}</td>
                    <td className="p-4 align-middle font-mono text-xs">{receipt.mill_tc}</td>
                    <td className="p-4 align-middle">
                      <div className="flex items-center justify-center gap-1">
                        {canModify(receipt) && (<>
                        <button
                          data-testid={`edit-rm-receipt-${idx}`}
                          onClick={() => handleEdit(receipt)}
                          className="p-2 hover:bg-blue-50 hover:text-blue-600 transition-all rounded-sm"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`delete-rm-receipt-${idx}`}
                          onClick={() => handleDelete(receipt.id)}
                          className="p-2 hover:bg-error/10 hover:text-error transition-all rounded-sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        </>)}
                      </div>
                    </td>
                  </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
